import React, { useEffect, useMemo, useRef, useState } from 'react';
import GovernmentCertificateCard from '../public/GovernmentCertificateCard';
import {
  getInputLanguage,
  INPUT_LANGUAGE_EVENT,
  INPUT_LANGUAGE_STORAGE_KEY,
} from '../../hooks/useMarathiInputTransliteration';
import { parseCertificateObject } from '../../utils/certificateTemplate';
import './CertificateBuilderWorkspace.css';

const TypingLanguageToggle = () => {
  const [typingLang, setTypingLang] = useState(getInputLanguage);

  useEffect(() => {
    const handleLangChange = event => {
      const next = event?.detail || getInputLanguage();
      setTypingLang(next);
    };
    window.addEventListener(INPUT_LANGUAGE_EVENT, handleLangChange);
    window.addEventListener('storage', handleLangChange);
    return () => {
      window.removeEventListener(INPUT_LANGUAGE_EVENT, handleLangChange);
      window.removeEventListener('storage', handleLangChange);
    };
  }, []);

  const toggleLang = next => {
    try {
      window.localStorage.setItem(INPUT_LANGUAGE_STORAGE_KEY, next);
    } catch {}
    setTypingLang(next);
    window.dispatchEvent(new CustomEvent(INPUT_LANGUAGE_EVENT, { detail: next }));
  };

  return (
    <div className="certificate-builder-typing-toggle" aria-label="टायपिंग भाषा निवडा">
      <span className="typing-label">टायपिंग</span>
      <button
        type="button"
        className={`typing-btn ${typingLang === 'mr' ? 'active' : ''}`}
        onClick={() => toggleLang('mr')}
        title="मराठी टायपिंग (English अक्षरे टाइप करून मराठी लिहा)"
        data-tooltip="मराठी टायपिंग"
      >
        मराठी
      </button>
      <button
        type="button"
        className={`typing-btn ${typingLang === 'en' ? 'active' : ''}`}
        onClick={() => toggleLang('en')}
        title="Type in English"
        data-tooltip="English typing"
      >
        English
      </button>
    </div>
  );
};

const BLOCK_META = {
  paragraph: { icon: 'T', label: 'Text', description: 'Bilingual paragraph' },
  notice: { icon: '!', label: 'Notice', description: 'Highlighted instruction' },
  field: { icon: '{}', label: 'Field', description: 'One application value' },
  field_grid: { icon: '▦', label: 'Field group', description: 'One or two columns' },
  party_panel: { icon: '◉◉', label: 'People', description: 'Two names and photos' },
  application_photo: { icon: '▣', label: 'Application photo', description: 'Changes per certificate' },
  artwork: { icon: '▧', label: 'Upload artwork', description: 'Public logo or seal' },
  tenant_logo: { icon: 'GP', label: 'GP logo', description: 'Gram Panchayat logo' },
  ashoka: { icon: 'IN', label: 'Ashoka emblem', description: 'Government emblem' },
};

const blockLabel = block => block?.type === 'field_grid'
  ? (block.items?.length === 1 ? 'Field' : `Field group (${block.items?.length || 0})`)
  : block?.type === 'party_panel' ? 'People panel'
    : block?.type === 'photo' ? 'Image'
      : block?.type === 'notice' ? 'Notice' : 'Text';

const GOVERNMENT_THEMES = [
  {
    id: 'maharashtra_royal',
    name_mr: '🏛️ महाराष्ट्र राजमुद्रा',
    name_en: 'Maharashtra Royal',
    accent: '#1a237e',
    page_background: '#ffffff',
    header_text: '#0f172a',
    title: '#1a237e',
    body_text: '#172033',
    body_secondary: '#64748b',
    variable: '#1a237e',
    footer_text: '#64748b',
    border: '#1a237e',
  },
  {
    id: 'emerald_green',
    name_mr: '🌿 ग्रामविकास हिरवा',
    name_en: 'Gram Vikas Emerald',
    accent: '#065f46',
    page_background: '#ffffff',
    header_text: '#064e3b',
    title: '#065f46',
    body_text: '#111827',
    body_secondary: '#4b5563',
    variable: '#047857',
    footer_text: '#4b5563',
    border: '#065f46',
  },
  {
    id: 'saffron_heritage',
    name_mr: '🚩 भगवा वारसा',
    name_en: 'Saffron Heritage',
    accent: '#9a3412',
    page_background: '#ffffff',
    header_text: '#7c2d12',
    title: '#9a3412',
    body_text: '#1c1917',
    body_secondary: '#78716c',
    variable: '#c2410c',
    footer_text: '#78716c',
    border: '#9a3412',
  },
  {
    id: 'official_slate',
    name_mr: '🏢 शासकीय क्लासिक',
    name_en: 'Official Slate',
    accent: '#1e293b',
    page_background: '#ffffff',
    header_text: '#0f172a',
    title: '#1e293b',
    body_text: '#1e293b',
    body_secondary: '#64748b',
    variable: '#2563eb',
    footer_text: '#64748b',
    border: '#334155',
  },
  {
    id: 'golden_ochre',
    name_mr: '⚜️ सुवर्ण ब्राँझ',
    name_en: 'Golden Ochre',
    accent: '#854d0e',
    page_background: '#ffffff',
    header_text: '#713f12',
    title: '#854d0e',
    body_text: '#1c1917',
    body_secondary: '#78716c',
    variable: '#a16207',
    footer_text: '#78716c',
    border: '#854d0e',
  },
];

const FONT_OPTIONS = [
  { id: 'Noto Sans Devanagari', label: 'Noto Sans Devanagari', hint: 'प्रमाण शासकीय (Standard)' },
  { id: 'Mukta', label: 'Mukta', hint: 'स्वच्छ व आधुनिक (Clean Sans)' },
  { id: 'Poppins', label: 'Poppins', hint: 'ठळक व सुबक (Bold Display)' },
  { id: 'Tiro Devanagari Marathi', label: 'Tiro Devanagari', hint: 'पारंपरिक (Traditional Serif)' },
  { id: 'Outfit', label: 'Outfit', hint: 'प्रीमियम (Premium Sans)' },
  { id: 'Inter', label: 'Inter', hint: 'स्पष्ट (Compact Sans)' },
];

const dynamicPhotoVariables = variables => variables.filter(variable => variable?.source === 'document');

const valueVariables = variables => variables.filter(variable => variable?.type !== 'image' && !String(variable.path || '').endsWith('_photo_url'));
const clone = value => JSON.parse(JSON.stringify(value));
const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
const CANVAS_WIDTH = 740;
const MIN_CANVAS_HEIGHT = 300;
const MAX_CANVAS_HEIGHT = 560;
const MAX_BODY_BLOCKS = 30;

const objectPathValue = (source, path) => String(path || '').split('.').reduce(
  (value, key) => (value === undefined || value === null ? undefined : value[key]),
  source,
);

const defaultHeight = block => {
  if (block.type === 'field_grid') {
    const cols = Math.max(1, Number(block.columns) || (block.items?.length > 1 ? 2 : 1));
    const rows = Math.ceil((block.items?.length || 1) / cols);
    return Math.max(40, rows * 40);
  }
  if (block.type === 'party_panel') return 110;
  if (block.type === 'photo') return 120;
  return block.type === 'notice' ? 70 : 50;
};

const preferredWidth = block => block.type === 'photo'
  ? 140
  : block.type === 'party_panel' || (block.type === 'field_grid' && Number(block.columns) === 2 && (block.items?.length || 1) > 2)
    ? CANVAS_WIDTH
    : 360;

const minimumBlockSize = block => {
  if (block?.type === 'party_panel') return { width: 280, height: 80 };
  if (block?.type === 'field_grid') return { width: 140, height: 35 };
  if (block?.type === 'photo') return { width: 60, height: 60 };
  return { width: 80, height: 30 };
};

const validLayout = (block, canvasHeight) => {
  const layout = block?.layout;
  if (!layout) return false;
  const minimum = minimumBlockSize(block);
  const x = Number(layout.x);
  const y = Number(layout.y);
  const width = Number(layout.width);
  const height = Number(layout.height);
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(width) && Number.isFinite(height)
    && x >= 0 && y >= 0 && width >= minimum.width && height >= minimum.height
    && x + width <= CANVAS_WIDTH && y + height <= canvasHeight;
};

const packBlocks = (blocks, requestedHeight) => {
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  const positioned = [];
  for (const [index, block] of blocks.entries()) {
    const width = preferredWidth(block);
    const height = defaultHeight(block);
    if (x > 0 && x + width > CANVAS_WIDTH) {
      x = 0;
      y += rowHeight + 10;
      rowHeight = 0;
    }
    if (y + height > MAX_CANVAS_HEIGHT) return null;
    positioned.push({ ...block, layout: { x, y, width, height, z_index: index } });
    x += width + 10;
    rowHeight = Math.max(rowHeight, height);
  }
  const contentBottom = positioned.reduce((bottom, block) => Math.max(bottom, block.layout.y + block.layout.height), MIN_CANVAS_HEIGHT);
  return {
    blocks: positioned,
    height: clamp(Math.max(Number(requestedHeight) || 520, contentBottom), MIN_CANVAS_HEIGHT, MAX_CANVAS_HEIGHT),
  };
};

const getDraftContentHash = targetDraft => {
  if (!targetDraft) return '';
  const schemaStr = JSON.stringify(targetDraft.template_schema || {});
  const nameMr = String(targetDraft.name_mr || '').trim();
  const nameEn = String(targetDraft.name_en || '').trim();
  const summary = String(targetDraft.change_summary || '').trim();
  return `${nameMr}::${nameEn}::${summary}::${schemaStr}`;
};

const ColorControl = ({ label, value, onChange }) => (
  <label className="certificate-builder-color">
    <span>{label}</span>
    <div><input type="color" value={value || '#ffffff'} onChange={event => onChange(event.target.value)} /><input value={value || ''} maxLength={7} onChange={event => onChange(event.target.value)} /></div>
  </label>
);

const Switch = ({ label, checked, onChange }) => (
  <label className="certificate-builder-switch">
    <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);

export default function CertificateBuilderWorkspace({
  draft,
  variables,
  previewContext,
  busy,
  editable,
  publishedUnlocked,
  activeSection,
  setActiveSection,
  updateSchemaValue,
  updateBlock,
  updateGridItem,
  updatePartySide,
  addGridItem,
  addLayoutBlock,
  uploadAsset,
  saveDraft,
  submitDraft,
  lockVersion,
  updateDraftValue,
}) {
  const [leftTab, setLeftTab] = useState('components');
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [layerDragId, setLayerDragId] = useState(null);
  const [previewScale, setPreviewScale] = useState(0.72);
  const [leftOpen, setLeftOpen] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [activeTextPath, setActiveTextPath] = useState('');
  const [builderNotice, setBuilderNotice] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const artworkInput = useRef(null);
  const headerArtworkInput = useRef(null);
  const isEditable = Boolean(editable ?? (draft && (['draft', 'rejected'].includes(draft.status) || publishedUnlocked)));
  const schema = draft.template_schema || {};
  const blocks = schema.blocks || [];
  const body = schema.body || {};
  const freeLayout = body.layout_mode === 'free';
  const headerFreeLayout = schema.header?.layout_mode === 'free';
  const canvasHeight = Number(body.canvas_height) || 520;
  const headerCanvasHeight = Number(schema.header?.canvas_height) || 180;
  const allSchemaBlocks = useMemo(() => [
    ...(schema.blocks || []).map(b => ({ ...b, _region: 'blocks' })),
    ...(schema.header?.content_blocks || []).map(b => ({ ...b, _region: 'header.content_blocks' })),
    ...(schema.footer?.content_blocks || []).map(b => ({ ...b, _region: 'footer.content_blocks' })),
  ], [schema.blocks, schema.header?.content_blocks, schema.footer?.content_blocks]);

  const selectedBlock = useMemo(() => {
    if (!selectedBlockId) return null;
    return allSchemaBlocks.find(b => b.id === selectedBlockId) || null;
  }, [allSchemaBlocks, selectedBlockId]);

  const photos = useMemo(() => dynamicPhotoVariables(variables), [variables]);
  const values = useMemo(() => valueVariables(variables), [variables]);
  const atBlockLimit = blocks.length >= MAX_BODY_BLOCKS;
  const activeContentRegion = activeSection === 'header'
    ? 'header.content_blocks'
    : activeSection === 'footer'
      ? 'footer.content_blocks'
      : activeSection === 'body' ? 'blocks' : null;
  const selectedRegion = selectedBlock?._region || activeContentRegion || 'blocks';
  const selectedRegionBlocks = Array.isArray(objectPathValue(schema, selectedRegion)) ? objectPathValue(schema, selectedRegion) : [];
  const selectedIndex = selectedRegionBlocks.findIndex(b => b.id === selectedBlockId);
  const activeContentBlocks = activeContentRegion ? (objectPathValue(schema, activeContentRegion) || []) : [];
  const activeContentLimit = activeContentRegion === 'blocks' ? MAX_BODY_BLOCKS : 10;
  const atActiveContentLimit = Boolean(activeContentRegion) && activeContentBlocks.length >= activeContentLimit;
  const activeRegionLabel = activeSection === 'header'
    ? 'Header'
    : activeSection === 'footer'
      ? 'Footer'
      : activeSection === 'body' ? 'Body' : 'Page';
  const canvasHint = activeSection === 'header'
    ? (headerFreeLayout ? 'Header is in freeform canvas mode. Drag components and move freely.' : 'Header content is structured. Drag components or use text fields to customize.')
    : activeSection === 'footer'
      ? 'Footer content is structured. Drag components or use text fields to customize.'
      : activeSection === 'page'
        ? 'Page settings control the overall A4 appearance, theme colors, and typography.'
        : freeLayout
          ? 'Drag components onto header, body, or footer. Use the round handle to move and the blue corner to resize.'
          : 'Automatic flow keeps body content in order. Drag components onto any section to add.';

  const toggleFullScreen = () => {
    setIsFullScreen(prev => !prev);
  };

  const pushHistory = currentSchema => {
    setHistory(prev => [...prev.slice(-25), clone(currentSchema)]);
    setFuture([]);
  };

  const handleUndo = () => {
    if (!history.length) return;
    const previous = history[history.length - 1];
    const newHistory = history.slice(0, -1);
    setFuture(prev => [clone(schema), ...prev.slice(0, 25)]);
    setHistory(newHistory);
    updateDraftValue('template_schema', previous);
  };

  const handleRedo = () => {
    if (!future.length) return;
    const next = future[0];
    const newFuture = future.slice(1);
    setHistory(prev => [...prev.slice(-25), clone(schema)]);
    setFuture(newFuture);
    updateDraftValue('template_schema', next);
  };

  const applyTheme = theme => {
    pushHistory(schema);
    const updated = clone(schema);
    if (!updated.page) updated.page = {};
    if (!updated.header) updated.header = {};
    if (!updated.body) updated.body = {};
    if (!updated.footer) updated.footer = {};

    updated.page.accent_color = theme.accent;
    updated.page.border_color = theme.border;
    updated.header.text_color = theme.header_text;
    updated.header.title_color = theme.title;
    updated.body.text_color = theme.body_text;
    updated.body.secondary_text_color = theme.body_secondary;
    updated.body.variable_color = theme.variable;
    updated.footer.text_color = theme.footer_text;
    updated.footer.accent_color = theme.accent;

    updateDraftValue('template_schema', updated);
    setBuilderNotice(`'${theme.name_mr}' रंगसंगती लागू झाली.`);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      const activeTag = document.activeElement?.tagName;
      const isEditable = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT' || document.activeElement?.isContentEditable;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        if (!isEditable) {
          event.preventDefault();
          handleUndo();
        }
      } else if (((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y')
        || ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'z')) {
        if (!isEditable) {
          event.preventDefault();
          handleRedo();
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleSaveDraft();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
        if (!isEditable && selectedBlockId) {
          event.preventDefault();
          duplicateSelected();
        }
      } else if ((event.ctrlKey || event.metaKey) && (event.key === '=' || event.key === '+')) {
        if (!isEditable) {
          event.preventDefault();
          setPreviewScale(scale => Math.min(2.5, Number((scale + 0.1).toFixed(2))));
        }
      } else if ((event.ctrlKey || event.metaKey) && (event.key === '-' || event.key === '_')) {
        if (!isEditable) {
          event.preventDefault();
          setPreviewScale(scale => Math.max(0.3, Number((scale - 0.1).toFixed(2))));
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        if (!isEditable) {
          event.preventDefault();
          setPreviewScale(1.0);
        }
      } else if (event.key === '?' || (event.key === '/' && event.shiftKey)) {
        if (!isEditable) {
          event.preventDefault();
          setShowShortcuts(v => !v);
        }
      } else if (event.key === 'Escape') {
        if (showShortcuts) {
          setShowShortcuts(false);
        } else if (isFullScreen) {
          setIsFullScreen(false);
        } else if (selectedBlockId) {
          setSelectedBlockId(null);
        }
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && selectedBlockId) {
        const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
        if (!isTyping && isEditable) {
          event.preventDefault();
          deleteSelected();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, selectedBlockId, selectedRegion, schema, history, future, showShortcuts, isEditable]);

  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const lastSavedSnapshotRef = useRef(getDraftContentHash(draft));
  const autoSaveTimerRef = useRef(null);
  const isSavingRef = useRef(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    setSelectedBlockId(null);
    setActiveTextPath('');
    setBuilderNotice('');
    setSaveStatus('idle');
    lastSavedSnapshotRef.current = getDraftContentHash(draft);
  }, [draft?.id]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      lastSavedSnapshotRef.current = getDraftContentHash(draft);
      return;
    }

    const currentHash = getDraftContentHash(draft);
    if (isSavingRef.current) {
      lastSavedSnapshotRef.current = currentHash;
      return;
    }

    const dirty = currentHash !== lastSavedSnapshotRef.current;

    if (!autoSaveEnabled || !isEditable || busy || !dirty) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (isSavingRef.current || !isEditable || busy) return;
      isSavingRef.current = true;
      setSaveStatus('saving');
      try {
        await saveDraft?.();
        lastSavedSnapshotRef.current = getDraftContentHash(draft);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (_err) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 4000);
      } finally {
        isSavingRef.current = false;
      }
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [draft, isEditable, busy, autoSaveEnabled, saveDraft]);

  const handleSaveDraft = async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    isSavingRef.current = true;
    setSaveStatus('saving');
    setBuilderNotice('');
    try {
      await saveDraft?.();
      lastSavedSnapshotRef.current = JSON.stringify(draft);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      setSaveStatus('error');
      const text = error?.response?.data?.error || error?.message || 'मसुदा जतन करताना त्रुटी आली.';
      const details = Array.isArray(error?.response?.data?.details)
        ? error.response.data.details.map(item => item.message).filter(Boolean).slice(0, 3).join('; ')
        : '';
      setBuilderNotice(details ? `${text}: ${details}` : text);
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      isSavingRef.current = false;
    }
  };

  const setBlocks = next => updateSchemaValue('blocks', next);
  const setRegionBlocks = (region, next) => {
    if (!['blocks', 'header.content_blocks', 'footer.content_blocks'].includes(region)) return;
    if (region === 'blocks') {
      setBlocks(next.map((block, index) => ({
        ...block,
        ...(block.layout ? { layout: { ...block.layout, z_index: index } } : {}),
      })));
      return;
    }
    updateSchemaValue(region, next);
  };
  const activateRegion = region => {
    setSelectedBlockId(null);
    setActiveTextPath('');
    setBuilderNotice('');
    setActiveSection(region);
  };
  const selectPreviewBlock = id => {
    if (!id) {
      activateRegion('body');
      return;
    }
    const bodyIndex = blocks.findIndex(block => block.id === id);
    const headerIndex = (schema.header?.content_blocks || []).findIndex(block => block.id === id);
    const footerIndex = (schema.footer?.content_blocks || []).findIndex(block => block.id === id);
    const region = bodyIndex >= 0 ? 'body' : headerIndex >= 0 ? 'header' : footerIndex >= 0 ? 'footer' : activeSection;
    const expectedPath = bodyIndex >= 0
      ? `blocks.${bodyIndex}.`
      : headerIndex >= 0
        ? `header.content_blocks.${headerIndex}.`
        : footerIndex >= 0 ? `footer.content_blocks.${footerIndex}.` : '';
    setSelectedBlockId(id);
    setActiveTextPath(current => expectedPath && current.startsWith(expectedPath) ? current : '');
    setActiveSection(region);
  };
  const activatePreviewText = path => {
    setActiveTextPath(path);
    const bodyMatch = String(path).match(/^blocks\.(\d+)/);
    if (bodyMatch) {
      setSelectedBlockId(blocks[Number(bodyMatch[1])]?.id || null);
      setActiveSection('body');
      return;
    }
    const regionMatch = String(path).match(/^(header|footer)\.content_blocks\.(\d+)/);
    if (regionMatch) {
      const regionBlocks = schema[regionMatch[1]]?.content_blocks || [];
      setSelectedBlockId(regionBlocks[Number(regionMatch[2])]?.id || null);
      setActiveSection(regionMatch[1]);
      return;
    }
    setSelectedBlockId(null);
    if (String(path).startsWith('header.')) setActiveSection('header');
    if (String(path).startsWith('footer.') || String(path).startsWith('signature.')) setActiveSection('footer');
  };
  const updateSelected = updater => {
    if (selectedIndex < 0 || !selectedRegion) return;
    const currentBlocks = Array.isArray(objectPathValue(schema, selectedRegion)) ? objectPathValue(schema, selectedRegion) : [];
    if (!currentBlocks[selectedIndex]) return;
    const updated = currentBlocks.map((b, i) => (i === selectedIndex ? typeof updater === 'function' ? updater(clone(b)) : updater : b));
    setRegionBlocks(selectedRegion, updated);
  };

  const selectNewBlock = (type, options = {}, region = 'blocks') => {
    const regionBlocks = objectPathValue(schema, region) || [];
    const regionLimit = region === 'blocks' ? MAX_BODY_BLOCKS : 10;
    const id = addLayoutBlock(region, type, options);
    if (!id) {
      setBuilderNotice(regionBlocks.length >= regionLimit
        ? `${region === 'blocks' ? 'Body' : region.startsWith('header') ? 'Header' : 'Footer'} can contain at most ${regionLimit} custom components.`
        : 'There is no open space for this component. Move or resize existing components, or increase the canvas height.');
      return;
    }
    setBuilderNotice('');
    if (region === 'blocks') {
      setSelectedBlockId(id);
      setActiveTextPath('');
      setActiveSection('body');
    } else {
      const targetSection = region.startsWith('header') ? 'header' : 'footer';
      updateSchemaValue(`${targetSection}.visible`, true);
      setSelectedBlockId(id);
      setActiveSection(targetSection);
      setActiveTextPath(`${region}.${regionBlocks.length}.text_mr`);
    }
  };

  const addComponent = (type, options = {}, requestedRegion = activeContentRegion) => {
    const targetRegion = requestedRegion || 'blocks';
    if (type === 'application_photo') {
      if (!photos.length) return;
      selectNewBlock('photo', { ...options, sourceType: 'field', variable: photos[0] }, targetRegion);
      return;
    }
    if (type === 'tenant_logo') { selectNewBlock('photo', { ...options, sourceType: 'tenant_logo' }, targetRegion); return; }
    if (type === 'ashoka') { selectNewBlock('photo', { ...options, sourceType: 'ashoka' }, targetRegion); return; }
    if (type === 'photo') { selectNewBlock('photo', options, targetRegion); return; }
    if (type === 'artwork') { artworkInput.current?.click(); return; }
    selectNewBlock(type, options, targetRegion);
  };

  const uploadArtwork = async (file, replaceSelected = false, targetRegion = activeContentRegion) => {
    const asset = await uploadAsset(file);
    if (!asset) return;
    if (replaceSelected && selectedBlock?.type === 'photo') {
      updateSelected(block => ({ ...block, source_type: 'upload', image_url: asset.url, photo_path: '' }));
      return;
    }
    selectNewBlock('photo', { sourceType: 'upload', imageUrl: asset.url }, targetRegion || 'blocks');
  };

  const uploadHeaderArtwork = async file => {
    const asset = await uploadAsset(file);
    if (!asset) return;
    updateSchemaValue('header.visible', true);
    updateSchemaValue('header.show_emblem', true);
    updateSchemaValue('header.emblem_source', 'custom');
    updateSchemaValue('header.emblem_url', asset.url);
    setBuilderNotice('Header artwork added.');
  };

  const setLayoutMode = mode => {
    setActiveTextPath('');
    if (mode === 'flow') {
      updateSchemaValue('body.layout_mode', 'flow');
      setBuilderNotice('');
      return;
    }
    if (!blocks.length) {
      updateSchemaValue('body.canvas_height', clamp(canvasHeight, MIN_CANVAS_HEIGHT, MAX_CANVAS_HEIGHT));
      updateSchemaValue('body.layout_mode', 'free');
      setBuilderNotice('');
      return;
    }
    const allLayoutsValid = blocks.every(block => validLayout(block, canvasHeight));
    const packed = allLayoutsValid
      ? { blocks: blocks.map((block, index) => ({ ...block, layout: { ...block.layout, z_index: index } })), height: canvasHeight }
      : packBlocks(blocks, canvasHeight);
    if (!packed) {
      setBuilderNotice('These components cannot fit safely on one free-layout page. Remove or combine some components, then try again.');
      return;
    }
    updateSchemaValue('body.canvas_height', packed.height);
    setBlocks(packed.blocks);
    updateSchemaValue('body.layout_mode', 'free');
    setBuilderNotice('');
  };

  const updateCanvasHeight = value => {
    const requested = clamp(Number(value) || MIN_CANVAS_HEIGHT, MIN_CANVAS_HEIGHT, MAX_CANVAS_HEIGHT);
    const required = blocks.reduce((bottom, block) => Math.max(
      bottom,
      Number(block.layout?.y || 0) + Number(block.layout?.height || 0),
    ), MIN_CANVAS_HEIGHT);
    if (requested < required) {
      updateSchemaValue('body.canvas_height', Math.min(required, MAX_CANVAS_HEIGHT));
      setBuilderNotice(`Canvas height cannot be smaller than ${Math.ceil(required)} while a component occupies that area.`);
      return;
    }
    updateSchemaValue('body.canvas_height', requested);
    setBuilderNotice('');
  };

  const duplicateSelected = () => {
    if (!selectedBlock) return;
    const targetRegion = selectedRegion;
    const currentBlocks = Array.isArray(objectPathValue(schema, targetRegion)) ? objectPathValue(schema, targetRegion) : [];
    const limit = targetRegion === 'blocks' ? MAX_BODY_BLOCKS : 10;
    if (currentBlocks.length >= limit) {
      setBuilderNotice(`या विभागात जास्तीत जास्त ${limit} घटक जोडता येतात.`);
      return;
    }
    pushHistory(schema);
    const copy = clone(selectedBlock);
    delete copy._region;
    copy.id = `${selectedBlock.id}-copy-${Date.now().toString(36)}`;
    const regionHeight = targetRegion.startsWith('header') ? headerCanvasHeight : canvasHeight;
    if (copy.layout) {
      copy.layout.x = clamp(Math.round((Number(copy.layout.x || 0) + 20) / 10) * 10, 0, 740 - Number(copy.layout.width || 80));
      copy.layout.y = clamp(Math.round((Number(copy.layout.y || 0) + 20) / 10) * 10, 0, regionHeight - Number(copy.layout.height || 40));
      copy.layout.z_index = currentBlocks.length;
    }
    setRegionBlocks(targetRegion, [...currentBlocks, copy]);
    setSelectedBlockId(copy.id);
    setActiveTextPath('');
    setBuilderNotice('घटक डुप्लिकेट केला.');
  };

  const deleteSelected = () => {
    if (!selectedBlockId) return;
    const targetRegion = selectedRegion;
    pushHistory(schema);
    const currentBlocks = Array.isArray(objectPathValue(schema, targetRegion)) ? objectPathValue(schema, targetRegion) : [];
    const updated = currentBlocks.filter(block => block.id !== selectedBlockId);
    setRegionBlocks(targetRegion, updated);
    setSelectedBlockId(null);
    setActiveTextPath('');
  };

  const reorderLayer = (region, fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const next = [...(objectPathValue(schema, region) || [])];
    const from = next.findIndex(block => block.id === fromId);
    const to = next.findIndex(block => block.id === toId);
    if (from < 0 || to < 0) return;
    pushHistory(schema);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRegionBlocks(region, next);
    setActiveTextPath('');
  };

  const alignSelected = alignment => {
    if (!selectedBlock?.layout) return;
    const targetRegion = selectedRegion;
    const regionHeight = targetRegion.startsWith('header') ? headerCanvasHeight : canvasHeight;
    pushHistory(schema);
    updateSelected(block => {
      const layout = { ...block.layout };
      const width = Number(layout.width) || 80;
      const height = Number(layout.height) || 40;
      if (alignment === 'left') layout.x = 0;
      else if (alignment === 'center' || alignment === 'center-h') layout.x = Math.round((740 - width) / 20) * 10;
      else if (alignment === 'right') layout.x = 740 - width;
      else if (alignment === 'top') layout.y = 0;
      else if (alignment === 'center-v') layout.y = Math.round((regionHeight - height) / 20) * 10;
      else if (alignment === 'bottom') layout.y = regionHeight - height;
      else if (alignment === 'full-width') {
        layout.x = 0;
        layout.width = 740;
      }
      return { ...block, layout };
    });
  };

  const bringForward = direction => {
    if (!selectedBlock) return;
    const targetRegion = selectedRegion;
    const currentBlocks = Array.isArray(objectPathValue(schema, targetRegion)) ? objectPathValue(schema, targetRegion) : [];
    const index = selectedIndex;
    const target = clamp(index + direction, 0, currentBlocks.length - 1);
    if (target === index) return;
    const next = [...currentBlocks];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setRegionBlocks(targetRegion, next.map((block, itemIndex) => ({ ...block, ...(block.layout ? { layout: { ...block.layout, z_index: itemIndex } } : {}) })));
    setActiveTextPath('');
  };

  const handleComponentDrag = (event, type) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/x-certificate-block', type);
  };

  const updateGeometry = (key, rawValue) => {
    if (!selectedBlock?.layout) return;
    updateSelected(block => {
      const current = { ...block.layout };
      const x = Number(current.x) || 0;
      const y = Number(current.y) || 0;
      const width = Number(current.width) || 80;
      const height = Number(current.height) || 40;
      const minimum = minimumBlockSize(block);
      const numeric = Number(rawValue) || 0;
      if (key === 'x') current.x = clamp(numeric, 0, CANVAS_WIDTH - width);
      if (key === 'y') current.y = clamp(numeric, 0, canvasHeight - height);
      if (key === 'width') current.width = clamp(numeric, minimum.width, CANVAS_WIDTH - x);
      if (key === 'height') current.height = clamp(numeric, minimum.height, canvasHeight - y);
      return { ...block, layout: current };
    });
  };

  const insertVariable = path => {
    const fallback = selectedBlock && ['paragraph', 'notice'].includes(selectedBlock.type)
      ? `blocks.${selectedIndex}.text_mr`
      : '';
    const target = activeTextPath || fallback;
    if (!target) {
      setBuilderNotice('Focus a Marathi or English text field first, then choose an application value.');
      return;
    }
    const current = String(objectPathValue(schema, target) || '');
    updateSchemaValue(target, `${current}${current && !/\s$/.test(current) ? ' ' : ''}{{${path}}}`);
    setBuilderNotice('');
  };

  const renderVariableInserter = () => (
    <details className="certificate-builder-variables">
      <summary>Insert application value{activeTextPath ? ` into ${activeTextPath.split('.').slice(-1)[0]}` : ''}</summary>
      <div>{values.map(variable => <button type="button" key={variable.path} onClick={() => insertVariable(variable.path)}>{variable.label}</button>)}</div>
    </details>
  );

  const updateRegionBlock = (region, index, updater) => {
    const regionBlocks = [...(objectPathValue(schema, region) || [])];
    regionBlocks[index] = updater(clone(regionBlocks[index]));
    updateSchemaValue(region, regionBlocks);
  };

  const moveRegionBlockTo = (region, from, to) => {
    const regionBlocks = [...(objectPathValue(schema, region) || [])];
    if (from < 0 || from >= regionBlocks.length || to < 0 || to >= regionBlocks.length || from === to) return;
    const [moved] = regionBlocks.splice(from, 1);
    regionBlocks.splice(to, 0, moved);
    setRegionBlocks(region, regionBlocks);
    setActiveTextPath('');
  };

  const moveRegionBlock = (region, from, direction) => moveRegionBlockTo(region, from, from + direction);

  const removeRegionBlock = (region, index) => {
    const regionBlocks = objectPathValue(schema, region) || [];
    const removedId = regionBlocks[index]?.id;
    setRegionBlocks(region, regionBlocks.filter((_block, itemIndex) => itemIndex !== index));
    if (removedId && removedId === selectedBlockId) setSelectedBlockId(null);
    setActiveTextPath('');
  };

  const renderRegionContent = (region, label) => {
    const regionBlocks = objectPathValue(schema, region) || [];
    return <details className="certificate-builder-region-content" open>
      <summary>{label} content <span>{regionBlocks.length}/10</span></summary>
      <p>Add optional text without placing editor controls inside the printed certificate.</p>
      {regionBlocks.map((block, index) => <article key={block.id || index}>
        <header><strong>{block.type === 'notice' ? 'Notice' : 'Text'} {index + 1}</strong><span><button type="button" aria-label={`Move ${label} block ${index + 1} up`} disabled={!index} onClick={() => moveRegionBlock(region, index, -1)}>↑</button><button type="button" aria-label={`Move ${label} block ${index + 1} down`} disabled={index === regionBlocks.length - 1} onClick={() => moveRegionBlock(region, index, 1)}>↓</button><button type="button" aria-label={`Remove ${label} block ${index + 1}`} onClick={() => removeRegionBlock(region, index)}>×</button></span></header>
        <label>Marathi text<textarea maxLength="1000" rows="3" value={block.text_mr || ''} onFocus={() => setActiveTextPath(`${region}.${index}.text_mr`)} onChange={event => updateRegionBlock(region, index, item => ({ ...item, text_mr: event.target.value }))} /></label>
        <label>English text<textarea maxLength="1000" rows="3" value={block.text_en || ''} onFocus={() => setActiveTextPath(`${region}.${index}.text_en`)} onChange={event => updateRegionBlock(region, index, item => ({ ...item, text_en: event.target.value }))} /></label>
      </article>)}
      <div className="certificate-builder-region-add"><button type="button" disabled={regionBlocks.length >= 10} onClick={() => selectNewBlock('paragraph', {}, region)}>+ Text</button><button type="button" disabled={regionBlocks.length >= 10} onClick={() => selectNewBlock('notice', {}, region)}>+ Notice</button></div>
      {renderVariableInserter()}
    </details>;
  };

  const renderSectionInspector = () => {
    if (activeSection === 'page') return <>
      <h3>Page &amp; Theme</h3><p className="certificate-builder-help">Overall A4 certificate appearance, colors, and typography.</p>
      
      <div className="certificate-builder-themes-box">
        <h4>शासकीय रंगसंगती (Government Themes)</h4>
        <p className="certificate-builder-help">एक-क्लिक मध्ये अधिकृत सरकारी थीम निवडा.</p>
        <div className="certificate-builder-theme-grid">
          {GOVERNMENT_THEMES.map(theme => (
            <button
              type="button"
              key={theme.id}
              className="certificate-builder-theme-btn"
              onClick={() => applyTheme(theme)}
              title={`${theme.name_mr} (${theme.name_en})`}
            >
              <div className="certificate-builder-theme-swatches">
                <span style={{ background: theme.accent }} />
                <span style={{ background: theme.header_text }} />
                <span style={{ background: theme.body_text }} />
                <span style={{ background: theme.border }} />
              </div>
              <span>{theme.name_mr}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="certificate-builder-typography-box">
        <h4>फॉन्ट व मजकूर रचना (Typography &amp; Fonts)</h4>
        <label>
          मुख्य फॉन्ट (Body Font Family)
          <select value={schema.page?.font_family || 'Noto Sans Devanagari'} onChange={event => updateSchemaValue('page.font_family', event.target.value)}>
            {FONT_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label} — {opt.hint}</option>
            ))}
          </select>
        </label>
        <label>
          शीर्षक फॉन्ट (Heading Font Family)
          <select value={schema.page?.heading_font_family || 'Noto Sans Devanagari'} onChange={event => updateSchemaValue('page.heading_font_family', event.target.value)}>
            {FONT_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label} — {opt.hint}</option>
            ))}
          </select>
        </label>
        <label>
          फॉन्ट आकार प्रमाण (Font Scale: {Math.round((schema.page?.font_scale || 1) * 100)}%)
          <input
            type="range"
            min="0.8"
            max="1.3"
            step="0.05"
            value={schema.page?.font_scale || 1}
            onChange={event => updateSchemaValue('page.font_scale', Number(event.target.value))}
          />
        </label>
      </div>

      <Switch
        label="Auto-save changes (स्वयंचलित जतन)"
        checked={autoSaveEnabled}
        onChange={setAutoSaveEnabled}
      />
      <label>Marathi template name<input value={draft.name_mr || ''} onChange={event => updateDraftValue('name_mr', event.target.value)} /></label>
      <label>English template name<input value={draft.name_en || ''} onChange={event => updateDraftValue('name_en', event.target.value)} /></label>
      <label>Change summary<input value={draft.change_summary || ''} onChange={event => updateDraftValue('change_summary', event.target.value)} /></label>
      <ColorControl label="Accent" value={schema.page?.accent_color} onChange={value => updateSchemaValue('page.accent_color', value)} />
      <ColorControl label="Page background" value={schema.page?.background_color} onChange={value => updateSchemaValue('page.background_color', value)} />
      <ColorControl label="Page text" value={schema.page?.text_color} onChange={value => updateSchemaValue('page.text_color', value)} />
      <ColorControl label="Border" value={schema.page?.border_color} onChange={value => updateSchemaValue('page.border_color', value)} />
      <label>Border width<input type="number" min="0" max="8" value={schema.page?.border_width ?? 2} onChange={event => updateSchemaValue('page.border_width', Number(event.target.value))} /></label>
      <label>Horizontal padding<input type="number" min="12" max="64" value={schema.page?.horizontal_padding ?? 40} onChange={event => updateSchemaValue('page.horizontal_padding', Number(event.target.value))} /></label>
    </>;
    if (activeSection === 'header') return <>
      <h3>Header</h3><p className="certificate-builder-help">Configure statutory headings or switch to freeform canvas.</p>
      <label className="certificate-builder-layout-select">
        <span>हेडर रचना (Header Mode)</span>
        <select value={schema.header?.layout_mode || 'flow'} onChange={event => updateSchemaValue('header.layout_mode', event.target.value)}>
          <option value="flow">क्लासिक शासकीय हेडर (Flow)</option>
          <option value="free">मुक्त कॅनव्हास हेडर (Freeform Canvas)</option>
        </select>
      </label>
      {schema.header?.layout_mode === 'free' && (
        <label>
          हेडर कॅनव्हास उंची (Header Height: {schema.header?.canvas_height || 180}px)
          <input
            type="range"
            min="120"
            max="350"
            step="10"
            value={schema.header?.canvas_height || 180}
            onChange={event => updateSchemaValue('header.canvas_height', Number(event.target.value))}
          />
        </label>
      )}
      <Switch label="Show header" checked={schema.header?.visible !== false} onChange={value => updateSchemaValue('header.visible', value)} />
      <Switch label="Government emblem" checked={schema.header?.show_emblem !== false} onChange={value => updateSchemaValue('header.show_emblem', value)} />
      <Switch label="Government name" checked={schema.header?.show_government_label !== false} onChange={value => updateSchemaValue('header.show_government_label', value)} />
      <Switch label="Authority reference" checked={schema.header?.show_authority_reference !== false} onChange={value => updateSchemaValue('header.show_authority_reference', value)} />
      <Switch label="Form label" checked={schema.header?.show_form_label !== false} onChange={value => updateSchemaValue('header.show_form_label', value)} />
      <Switch label="Certificate title" checked={schema.header?.show_title !== false} onChange={value => updateSchemaValue('header.show_title', value)} />
      <Switch label="Legal reference" checked={schema.header?.show_legal_reference !== false} onChange={value => updateSchemaValue('header.show_legal_reference', value)} />
      <Switch label="Certificate number" checked={schema.header?.show_certificate_number !== false} onChange={value => updateSchemaValue('header.show_certificate_number', value)} />
      <label>Alignment<select value={schema.header?.alignment || 'center'} onChange={event => updateSchemaValue('header.alignment', event.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
      <label>Emblem source<select value={schema.header?.emblem_source || 'ashoka'} onChange={event => {
        const source = event.target.value;
        if (source === 'custom' && !schema.header?.emblem_url) {
          setBuilderNotice('Choose a PNG or JPEG header artwork file below.');
          return;
        }
        updateSchemaValue('header.emblem_source', source);
      }}><option value="ashoka">Ashoka emblem</option><option value="tenant_logo">Gram Panchayat logo</option>{(schema.header?.emblem_url || schema.header?.emblem_source === 'custom') && <option value="custom">Uploaded artwork</option>}</select></label>
      <label className="certificate-builder-upload">Upload header artwork<input type="file" accept="image/png,image/jpeg" onChange={event => { uploadHeaderArtwork(event.target.files?.[0]); event.target.value = ''; }} /><small>Public PNG/JPEG logo or seal, maximum 2MB. Uploading selects it as the emblem.</small></label>
      <ColorControl label="Header background" value={schema.header?.background_color} onChange={value => updateSchemaValue('header.background_color', value)} />
      <ColorControl label="Header text" value={schema.header?.text_color} onChange={value => updateSchemaValue('header.text_color', value)} />
      <ColorControl label="Title color" value={schema.header?.title_color} onChange={value => updateSchemaValue('header.title_color', value)} />
      <label>Marathi government name<input maxLength="500" value={schema.header?.government_label_mr || ''} onFocus={() => setActiveTextPath('header.government_label_mr')} onChange={event => updateSchemaValue('header.government_label_mr', event.target.value)} /></label>
      <label>English government name<input maxLength="500" value={schema.header?.government_label_en || ''} onFocus={() => setActiveTextPath('header.government_label_en')} onChange={event => updateSchemaValue('header.government_label_en', event.target.value)} /></label>
      <label>Marathi title<input maxLength="500" value={schema.header?.title_mr || ''} onFocus={() => setActiveTextPath('header.title_mr')} onChange={event => updateSchemaValue('header.title_mr', event.target.value)} /></label>
      <label>English title<input maxLength="500" value={schema.header?.title_en || ''} onFocus={() => setActiveTextPath('header.title_en')} onChange={event => updateSchemaValue('header.title_en', event.target.value)} /></label>
      <label>Marathi form label<input maxLength="500" value={schema.header?.form_label_mr || ''} onFocus={() => setActiveTextPath('header.form_label_mr')} onChange={event => updateSchemaValue('header.form_label_mr', event.target.value)} /></label>
      <label>English form label<input maxLength="500" value={schema.header?.form_label_en || ''} onFocus={() => setActiveTextPath('header.form_label_en')} onChange={event => updateSchemaValue('header.form_label_en', event.target.value)} /></label>
      <label>Marathi authority reference<textarea maxLength="500" rows="3" value={schema.header?.authority_reference_mr || ''} onFocus={() => setActiveTextPath('header.authority_reference_mr')} onChange={event => updateSchemaValue('header.authority_reference_mr', event.target.value)} /></label>
      <label>English authority reference<textarea maxLength="500" rows="3" value={schema.header?.authority_reference_en || ''} onFocus={() => setActiveTextPath('header.authority_reference_en')} onChange={event => updateSchemaValue('header.authority_reference_en', event.target.value)} /></label>
      {renderRegionContent('header.content_blocks', 'Header')}
    </>;
    if (activeSection === 'footer') return <>
      <h3>Footer</h3><p className="certificate-builder-help">Signatures and verification remain protected in a structured footer.</p>
      <Switch label="Show footer" checked={schema.footer?.visible !== false} onChange={value => updateSchemaValue('footer.visible', value)} />
      <Switch label="Place and date" checked={schema.footer?.show_place_date !== false} onChange={value => updateSchemaValue('footer.show_place_date', value)} />
      <Switch label="QR verification" checked={schema.footer?.show_qr !== false} onChange={value => updateSchemaValue('footer.show_qr', value)} />
      <Switch label="Left signature" checked={schema.footer?.show_left_signature !== false} onChange={value => updateSchemaValue('footer.show_left_signature', value)} />
      <Switch label="Right signature" checked={schema.footer?.show_right_signature !== false} onChange={value => updateSchemaValue('footer.show_right_signature', value)} />
      <Switch label="Verification text" checked={schema.footer?.show_verification !== false} onChange={value => updateSchemaValue('footer.show_verification', value)} />
      <ColorControl label="Footer background" value={schema.footer?.background_color} onChange={value => updateSchemaValue('footer.background_color', value)} />
      <ColorControl label="Footer text" value={schema.footer?.text_color} onChange={value => updateSchemaValue('footer.text_color', value)} />
      <ColorControl label="Signature accent" value={schema.footer?.accent_color} onChange={value => updateSchemaValue('footer.accent_color', value)} />
      <label>Left signature (Marathi)<input value={schema.signature?.left_label_mr || ''} onChange={event => updateSchemaValue('signature.left_label_mr', event.target.value)} /></label>
      <label>Left signature (English)<input value={schema.signature?.left_label_en || ''} onChange={event => updateSchemaValue('signature.left_label_en', event.target.value)} /></label>
      <label>Right signature (Marathi)<input value={schema.signature?.right_label_mr || ''} onChange={event => updateSchemaValue('signature.right_label_mr', event.target.value)} /></label>
      <label>Right signature (English)<input value={schema.signature?.right_label_en || ''} onChange={event => updateSchemaValue('signature.right_label_en', event.target.value)} /></label>
      <label>Verification (Marathi)<textarea maxLength="500" rows="3" value={schema.footer?.verification_text_mr || ''} onFocus={() => setActiveTextPath('footer.verification_text_mr')} onChange={event => updateSchemaValue('footer.verification_text_mr', event.target.value)} /></label>
      <label>Verification (English)<textarea maxLength="500" rows="3" value={schema.footer?.verification_text_en || ''} onFocus={() => setActiveTextPath('footer.verification_text_en')} onChange={event => updateSchemaValue('footer.verification_text_en', event.target.value)} /></label>
      {renderRegionContent('footer.content_blocks', 'Footer')}
    </>;
    return <>
      <h3>Body canvas</h3><p className="certificate-builder-help">Use flow for automatic layout or free layout for drag-and-resize placement.</p>
      <label>Layout mode<select value={body.layout_mode || 'flow'} onChange={event => setLayoutMode(event.target.value)}><option value="flow">Automatic flow</option><option value="free">Free drag &amp; drop</option></select></label>
      {freeLayout && <label>Canvas height<input type="number" min="300" max="560" value={canvasHeight} onChange={event => updateCanvasHeight(event.target.value)} /></label>}
      <label>Text alignment<select value={body.alignment || 'left'} onChange={event => updateSchemaValue('body.alignment', event.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
      <label>Block spacing<input type="number" min="0" max="32" value={body.block_gap ?? 9} onChange={event => updateSchemaValue('body.block_gap', Number(event.target.value))} /></label>
      <Switch label="Field separator lines" checked={body.show_field_borders === true} onChange={value => updateSchemaValue('body.show_field_borders', value)} />
      <ColorControl label="Body background" value={body.background_color} onChange={value => updateSchemaValue('body.background_color', value)} />
      <ColorControl label="Primary text" value={body.text_color} onChange={value => updateSchemaValue('body.text_color', value)} />
      <ColorControl label="Secondary text" value={body.secondary_text_color} onChange={value => updateSchemaValue('body.secondary_text_color', value)} />
      <ColorControl label="Variable values" value={body.variable_color} onChange={value => updateSchemaValue('body.variable_color', value)} />
      <ColorControl label="Field background" value={body.field_background_color} onChange={value => updateSchemaValue('body.field_background_color', value)} />
      <ColorControl label="Field/photo borders" value={body.field_border_color} onChange={value => updateSchemaValue('body.field_border_color', value)} />
    </>;
  };

  const renderBlockInspector = () => {
    if (!selectedBlock) return renderSectionInspector();
    const layout = selectedBlock.layout || {};
    return <>
      <div className="certificate-builder-inspector-title"><div><small>Selected component</small><h3>{blockLabel(selectedBlock)}</h3></div><button type="button" onClick={() => { setSelectedBlockId(null); setActiveTextPath(''); }} aria-label="Close component inspector">×</button></div>
      {freeLayout && <div className="certificate-builder-geometry">
        {['x', 'y', 'width', 'height'].map(key => {
          const minimum = minimumBlockSize(selectedBlock);
          const maximum = key === 'x' ? CANVAS_WIDTH - Number(layout.width || 80)
            : key === 'y' ? canvasHeight - Number(layout.height || 40)
              : key === 'width' ? CANVAS_WIDTH - Number(layout.x || 0)
                : canvasHeight - Number(layout.y || 0);
          return <label key={key}>{key.toUpperCase()}<input type="number" value={Number(layout[key]) || 0} min={key === 'width' ? minimum.width : key === 'height' ? minimum.height : 0} max={maximum} onChange={event => updateGeometry(key, event.target.value)} /></label>;
        })}
      </div>}
      {selectedBlock.type === 'field_grid' && <>
        <label>Columns<select value={selectedBlock.columns || 2} onChange={event => updateSelected(block => ({ ...block, columns: Number(event.target.value) }))}><option value="1">One</option><option value="2">Two</option></select></label>
        <div className="certificate-builder-field-list">{(selectedBlock.items || []).map((item, fieldIndex) => <article key={`${item.path}-${fieldIndex}`}>
          <div><strong>Field {fieldIndex + 1}</strong><button type="button" disabled={(selectedBlock.items || []).length <= 1} onClick={() => updateSelected(block => ({ ...block, items: block.items.filter((_entry, index) => index !== fieldIndex) }))} aria-label={`Remove field ${fieldIndex + 1}`}>×</button></div>
          <label>Application value<select value={item.path || ''} onChange={event => updateGridItem(selectedIndex, fieldIndex, 'path', event.target.value)}>{values.map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}</select></label>
          <label>Marathi label<input maxLength="200" value={item.label_mr || ''} onChange={event => updateGridItem(selectedIndex, fieldIndex, 'label_mr', event.target.value)} /></label>
          <label>Format<select value={item.format || 'text'} onChange={event => updateGridItem(selectedIndex, fieldIndex, 'format', event.target.value)}>{['text', 'uppercase', 'masked', 'date', 'date_mr', 'date_en', 'number', 'marathi_digits', 'currency', 'percentage', 'gender'].map(format => <option key={format}>{format}</option>)}</select></label>
          <Switch label="Mask value (e.g. XXXX-XXXX-1234)" checked={Boolean(item.masked)} onChange={value => updateGridItem(selectedIndex, fieldIndex, 'masked', value)} />
          <Switch label="Hide when empty" checked={Boolean(item.optional)} onChange={value => updateGridItem(selectedIndex, fieldIndex, 'optional', value)} />
        </article>)}</div>
        <button type="button" className="certificate-builder-secondary" disabled={(selectedBlock.items || []).length >= 30} onClick={() => addGridItem(selectedIndex)}>+ Add another field</button>
      </>}
      {['paragraph', 'notice'].includes(selectedBlock.type) && <>
        <label>Marathi text<textarea maxLength="1000" rows="5" value={selectedBlock.text_mr || ''} onFocus={() => setActiveTextPath(`blocks.${selectedIndex}.text_mr`)} onChange={event => updateSelected(block => ({ ...block, text_mr: event.target.value }))} /></label>
        <label>English text<textarea maxLength="1000" rows="5" value={selectedBlock.text_en || ''} onFocus={() => setActiveTextPath(`blocks.${selectedIndex}.text_en`)} onChange={event => updateSelected(block => ({ ...block, text_en: event.target.value }))} /></label>
        {renderVariableInserter()}
      </>}
      {selectedBlock.type === 'photo' && <>
        <label>Image source<select value={selectedBlock.source_type || 'field'} onChange={event => {
          const sourceType = event.target.value;
          updateSelected(block => ({
            ...block,
            source_type: sourceType,
            photo_path: sourceType === 'field' ? block.photo_path || photos[0]?.path || '' : '',
            image_url: sourceType === 'upload' ? block.image_url || '' : '',
          }));
        }}><option value="field">Application photo/document</option><option value="upload">Uploaded public artwork</option><option value="tenant_logo">Gram Panchayat logo</option><option value="ashoka">Ashoka emblem</option></select></label>
        {(selectedBlock.source_type || 'field') === 'field' && <label>Application photo<select value={selectedBlock.photo_path || ''} onChange={event => {
          const variable = photos.find(item => item.path === event.target.value);
          updateSelected(block => ({ ...block, photo_path: event.target.value, ...(!block.caption_mr && variable ? { caption_mr: variable.label || '', caption_en: variable.label_en || 'Applicant photograph' } : {}) }));
        }}><option value="">Choose a configured photo</option>{photos.map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}</select><small>Per-certificate photo from a configured application document.</small></label>}
        {selectedBlock.source_type === 'upload' && <label className="certificate-builder-upload">Public artwork<input type="file" accept="image/png,image/jpeg" onChange={event => { uploadArtwork(event.target.files?.[0], true); event.target.value = ''; }} /><small>Use only reusable logos or seals—not citizen photos.</small></label>}
        <label>Fit<select value={selectedBlock.fit || 'cover'} onChange={event => updateSelected(block => ({ ...block, fit: event.target.value }))}><option value="contain">Show full image</option><option value="cover">Fill frame</option></select></label>
        <label>Marathi caption<input maxLength="200" value={selectedBlock.caption_mr || ''} onFocus={() => setActiveTextPath(`blocks.${selectedIndex}.caption_mr`)} onChange={event => updateSelected(block => ({ ...block, caption_mr: event.target.value }))} /></label>
        <label>English caption<input maxLength="200" value={selectedBlock.caption_en || ''} onFocus={() => setActiveTextPath(`blocks.${selectedIndex}.caption_en`)} onChange={event => updateSelected(block => ({ ...block, caption_en: event.target.value }))} /></label>
        <Switch label="Show image border" checked={selectedBlock.show_border !== false} onChange={value => updateSelected(block => ({ ...block, show_border: value }))} />
        <Switch label="Hide when missing" checked={Boolean(selectedBlock.optional)} onChange={value => updateSelected(block => ({ ...block, optional: value }))} />
      </>}
      {selectedBlock.type === 'party_panel' && <div className="certificate-builder-parties">{['left', 'right'].map(side => <fieldset key={side}><legend>{side === 'left' ? 'Left person' : 'Right person'}</legend><label>Marathi label<input maxLength="200" value={selectedBlock[side]?.label_mr || ''} onChange={event => updatePartySide(selectedIndex, side, 'label_mr', event.target.value)} /></label><label>English label<input maxLength="200" value={selectedBlock[side]?.label_en || ''} onChange={event => updatePartySide(selectedIndex, side, 'label_en', event.target.value)} /></label><label>Name<select value={selectedBlock[side]?.name_path || ''} onChange={event => updatePartySide(selectedIndex, side, 'name_path', event.target.value)}>{values.map(variable => <option key={variable.path} value={variable.path}>{variable.label}</option>)}</select></label><label>Photo<select value={selectedBlock[side]?.photo_path || ''} onChange={event => updatePartySide(selectedIndex, side, 'photo_path', event.target.value)}><option value="">No photo</option>{photos.map(variable => <option key={variable.path} value={variable.path}>{variable.label}</option>)}</select></label></fieldset>)}</div>}
    </>;
  };

  const regionKeys = ['page', 'header', 'body', 'footer'];
  const handleRegionKeyDown = event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = regionKeys.indexOf(activeSection);
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? regionKeys.length - 1
        : (current + (event.key === 'ArrowRight' ? 1 : -1) + regionKeys.length) % regionKeys.length;
    setSelectedBlockId(null);
    setActiveTextPath('');
    setBuilderNotice('');
    setActiveSection(regionKeys[next]);
    event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  const regionTooltipText = {
    page: 'Page settings (margins, colors)',
    header: 'Header section configuration',
    body: 'Body section configuration',
    footer: 'Footer section configuration',
  };

  return <section className={`certificate-builder ${isFullScreen ? 'certificate-builder-fullscreen' : ''}`}>
    <header className="certificate-builder-toolbar">
      <div className="certificate-builder-toolbar-row">
        <div className="certificate-builder-toolbar-group">
          <button
            type="button"
            aria-label={leftOpen ? 'Hide components panel' : 'Show components panel'}
            title={leftOpen ? 'Hide components panel' : 'Show components panel'}
            data-tooltip={leftOpen ? 'Hide components panel' : 'Show components panel'}
            aria-pressed={leftOpen}
            className={leftOpen ? 'active' : ''}
            onClick={() => setLeftOpen(value => !value)}
          >📁 Components</button>
          <button
            type="button"
            aria-label="Undo last change (Ctrl+Z)"
            title="पूर्ववत करा / Undo (Ctrl+Z)"
            data-tooltip="पूर्ववत करा (Ctrl+Z)"
            disabled={!history.length}
            onClick={handleUndo}
          >↶ Undo</button>
          <button
            type="button"
            aria-label="Redo last change (Ctrl+Y)"
            title="पुन्हा करा / Redo (Ctrl+Y)"
            data-tooltip="पुन्हा करा (Ctrl+Y)"
            disabled={!future.length}
            onClick={handleRedo}
          >↷ Redo</button>
        </div>

        <div className="certificate-builder-toolbar-spacer" />

        <TypingLanguageToggle />

        <div className="certificate-builder-zoom">
          <button type="button" onClick={() => setPreviewScale(scale => Math.max(.3, Number((scale - .05).toFixed(2))))} aria-label="Zoom preview out" title="Zoom preview out (−)" data-tooltip="Zoom out (−)">−</button>
          <strong title="Current preview zoom" data-tooltip="Current zoom">{Math.round(previewScale * 100)}%</strong>
          <button type="button" onClick={() => setPreviewScale(scale => Math.min(2.5, Number((scale + .05).toFixed(2))))} aria-label="Zoom preview in" title="Zoom preview in (+ / up to 250%)" data-tooltip="Zoom in (+)">+</button>
          <button type="button" aria-label="Fit certificate preview" onClick={() => setPreviewScale(.72)} title="Reset preview scale to fit view (72%)" data-tooltip="Fit preview (72%)">Fit</button>
        </div>

        <button
          type="button"
          className="certificate-builder-shortcuts-btn"
          onClick={() => setShowShortcuts(true)}
          aria-label="Show keyboard shortcuts (?)"
          title="कीबोर्ड शॉर्टकट्स / Keyboard shortcuts (?)"
          data-tooltip="शॉर्टकट्स (?)"
        >
          ⌨ ?
        </button>
        <button
          type="button"
          className={`certificate-builder-fullscreen-btn ${isFullScreen ? 'active' : ''}`}
          onClick={toggleFullScreen}
          aria-label={isFullScreen ? 'Exit full screen mode' : 'Enter full screen mode'}
          title={isFullScreen ? 'Exit full screen mode (Esc)' : 'Configure certificate in full screen mode'}
          data-tooltip={isFullScreen ? 'Exit full screen' : 'Full screen mode'}
        >
          {isFullScreen ? '⤓ Exit' : '⛶ Full'}
        </button>
        <button
          type="button"
          aria-label={inspectorOpen ? 'Hide settings panel' : 'Show settings panel'}
          title={inspectorOpen ? 'Hide settings panel' : 'Show settings panel'}
          data-tooltip={inspectorOpen ? 'Hide settings panel' : 'Show settings panel'}
          aria-pressed={inspectorOpen}
          className={inspectorOpen ? 'active' : ''}
          onClick={() => setInspectorOpen(value => !value)}
        >⚙ Settings</button>
        <button
          type="button"
          className={`certificate-builder-save ${saveStatus === 'saved' ? 'saved' : ''} ${saveStatus === 'saving' ? 'saving' : ''}`}
          onClick={handleSaveDraft}
          disabled={busy || saveStatus === 'saving'}
          title={publishedUnlocked ? 'Save changes to template' : 'Save template draft'}
          data-tooltip={saveStatus === 'saved' ? 'बदल यशस्वीरीत्या जतन झाले!' : publishedUnlocked ? 'Save changes' : 'Save draft'}
          data-tooltip-pos="left"
        >
          {saveStatus === 'saving'
            ? '⏳ जतन होत आहे...'
            : saveStatus === 'saved'
              ? '✓ जतन झाले'
              : publishedUnlocked ? 'Save changes' : 'Save draft'}
        </button>
      </div>

      {(activeSection === 'body' || (activeSection === 'header' && headerFreeLayout)) && (
        <div className="certificate-builder-toolbar-tools">
          {activeSection === 'body' && (
            <label className="certificate-builder-layout-select" title="Switch layout mode (Flow or Free drag & drop)" data-tooltip="Switch layout mode">
              <span>Layout</span>
              <select value={body.layout_mode || 'flow'} onChange={event => setLayoutMode(event.target.value)}>
                <option value="flow">Automatic flow</option>
                <option value="free">Free drag &amp; drop</option>
              </select>
            </label>
          )}
          <div className="certificate-builder-toolbar-group">
            <button type="button" aria-label="Duplicate selected component" onClick={duplicateSelected} disabled={!selectedBlock} title="Duplicate selected component (Ctrl+D)" data-tooltip="Duplicate (Ctrl+D)">⧉ Duplicate</button>
            <button type="button" aria-label="Delete selected component" onClick={deleteSelected} disabled={!selectedBlock} title="Delete selected component (Delete)" data-tooltip="Delete component">⌫ Delete</button>
            <button type="button" aria-label="Send selected component backward" onClick={() => bringForward(-1)} disabled={!selectedBlock} title="Send selected component backward" data-tooltip="Send backward">↓ Layer</button>
            <button type="button" aria-label="Bring selected component forward" onClick={() => bringForward(1)} disabled={!selectedBlock} title="Bring selected component forward" data-tooltip="Bring forward">↑ Layer</button>
          </div>
          {(freeLayout || headerFreeLayout) && <>
            <div className="certificate-builder-toolbar-divider" />
            <div className="certificate-builder-toolbar-group certificate-builder-align-tools">
              <button type="button" aria-label="Align selected component left" onClick={() => alignSelected('left')} disabled={!selectedBlock} title="Align left" data-tooltip="Align left">⭰</button>
              <button type="button" aria-label="Center selected component" onClick={() => alignSelected('center-h')} disabled={!selectedBlock} title="Center horizontally" data-tooltip="Center horizontally">⭲⭰</button>
              <button type="button" aria-label="Align selected component right" onClick={() => alignSelected('right')} disabled={!selectedBlock} title="Align right" data-tooltip="Align right">⭲</button>
              <button type="button" aria-label="Align selected component top" onClick={() => alignSelected('top')} disabled={!selectedBlock} title="Align top" data-tooltip="Align top">⭱</button>
              <button type="button" aria-label="Center selected component vertically" onClick={() => alignSelected('center-v')} disabled={!selectedBlock} title="Center vertically" data-tooltip="Center vertically">⭳⭱</button>
              <button type="button" aria-label="Align selected component bottom" onClick={() => alignSelected('bottom')} disabled={!selectedBlock} title="Align bottom" data-tooltip="Align bottom">⭳</button>
              <button type="button" aria-label="Expand selected component to full width" onClick={() => alignSelected('full-width')} disabled={!selectedBlock} title="Full width" data-tooltip="Full width">⬌</button>
            </div>
          </>}
          {selectedBlock && (
            <div className="certificate-builder-selected-indicator">
              <span className="indicator-dot" />
              <span>{BLOCK_META[selectedBlock.type]?.label || selectedBlock.type}</span>
            </div>
          )}
        </div>
      )}
    </header>

    {builderNotice && <div className="certificate-builder-notice" role="status">{builderNotice}<button type="button" onClick={() => setBuilderNotice('')} aria-label="Dismiss builder message">×</button></div>}
    <div className={`certificate-builder-grid ${leftOpen ? '' : 'left-closed'} ${inspectorOpen ? '' : 'inspector-closed'}`}>
      {leftOpen && <aside className="certificate-builder-left">
        <div className="certificate-builder-region-nav" role="tablist" aria-label="Certificate layout regions" onKeyDown={handleRegionKeyDown}>
          {[['page', 'Page', '📄'], ['header', 'Header', '🏛️'], ['body', 'Body', '📑'], ['footer', 'Footer', '✍️']].map(([key, label, icon]) => (
            <button
              type="button"
              id={`certificate-builder-tab-${key}`}
              aria-controls="certificate-builder-inspector"
              role="tab"
              aria-label={label}
              aria-selected={activeSection === key}
              className={`certificate-builder-region-btn ${activeSection === key ? 'active' : ''}`}
              key={key}
              title={regionTooltipText[key]}
              data-tooltip={regionTooltipText[key]}
              onClick={() => activateRegion(key)}
            >
              <span className="region-icon" aria-hidden="true">{icon}</span>
              <span className="region-name">{label}</span>
            </button>
          ))}
        </div>
        <div className="certificate-builder-side-tabs">
          <button type="button" className={leftTab === 'components' ? 'active' : ''} onClick={() => setLeftTab('components')}>Components</button>
          <button type="button" className={leftTab === 'layers' ? 'active' : ''} onClick={() => setLeftTab('layers')}>Layers</button>
          <button type="button" className={leftTab === 'data' ? 'active' : ''} onClick={() => setLeftTab('data')}>Data</button>
        </div>
        {leftTab === 'components' && <div className="certificate-builder-components">
          <p><strong>{activeSection === 'header' ? 'Adding to Header' : activeSection === 'footer' ? 'Adding to Footer' : activeSection === 'body' ? 'Adding to Body' : 'Choose a content region'}</strong><br />{activeContentRegion === 'blocks' ? 'Click to add, or drag onto any certificate section.' : activeContentRegion ? 'Click a supported component to add, or drag onto header/body/footer.' : 'Select Header, Body, or Footer in the toolbar.'}</p>
          {Object.entries(BLOCK_META)
            .filter(([type]) => Boolean(activeContentRegion))
            .map(([type, meta]) => <button
              type="button"
              draggable={!atActiveContentLimit && type !== 'artwork'}
              onDragStart={event => handleComponentDrag(event, type)}
              key={type}
              disabled={atActiveContentLimit || (type === 'application_photo' && !photos.length)}
              onClick={() => type === 'artwork' ? artworkInput.current?.click() : addComponent(type)}
            ><span>{meta.icon}</span><div><strong>{meta.label}</strong><small>{meta.description}</small></div></button>)}
          {activeSection === 'header' && <div className="certificate-builder-context-assets">
            <strong>Header emblem</strong>
            <button type="button" onClick={() => { updateSchemaValue('header.visible', true); updateSchemaValue('header.show_emblem', true); updateSchemaValue('header.emblem_source', 'ashoka'); }}><span>IN</span><div><strong>Ashoka emblem</strong><small>Use the government emblem</small></div></button>
            <button type="button" onClick={() => { updateSchemaValue('header.visible', true); updateSchemaValue('header.show_emblem', true); updateSchemaValue('header.emblem_source', 'tenant_logo'); }}><span>GP</span><div><strong>GP logo</strong><small>Use the Gram Panchayat logo</small></div></button>
            <button type="button" onClick={() => headerArtworkInput.current?.click()}><span>▧</span><div><strong>Upload header artwork</strong><small>Public PNG/JPEG logo or seal</small></div></button>
          </div>}
          <input ref={artworkInput} className="certificate-builder-file-input" type="file" accept="image/png,image/jpeg" onChange={event => { uploadArtwork(event.target.files?.[0]); event.target.value = ''; }} />
          <input ref={headerArtworkInput} className="certificate-builder-file-input" type="file" accept="image/png,image/jpeg" onChange={event => { uploadHeaderArtwork(event.target.files?.[0]); event.target.value = ''; }} />
        </div>}
        {leftTab === 'layers' && <div className="certificate-builder-layers">
          <p>{activeContentRegion
            ? `Drag to reorder ${activeRegionLabel.toLowerCase()} custom components. Header and Footer remain structured; Body layers also control stacking in free layout.`
            : 'Choose Header, Body, or Footer to view its custom components.'}</p>
          {activeContentBlocks.map((block, index) => <button
            type="button"
            key={block.id}
            draggable
            aria-label={`Select ${activeRegionLabel} ${blockLabel(block)} ${index + 1}`}
            onDragStart={() => setLayerDragId({ region: activeContentRegion, id: block.id })}
            onDragEnd={() => setLayerDragId(null)}
            onDragOver={event => event.preventDefault()}
            onDrop={() => {
              if (layerDragId?.region === activeContentRegion) reorderLayer(activeContentRegion, layerDragId.id, block.id);
              setLayerDragId(null);
            }}
            className={selectedBlockId === block.id ? 'selected' : ''}
            onClick={() => selectPreviewBlock(block.id)}
          ><span>⠿</span><div><strong>{blockLabel(block)}</strong><small>{activeRegionLabel} position {index + 1}</small></div></button>)}
          {activeContentRegion && !activeContentBlocks.length && <p>No {activeRegionLabel.toLowerCase()} custom components yet.</p>}
        </div>}
        {leftTab === 'data' && <div className="certificate-builder-data">
          <p>{activeContentRegion === 'blocks' ? 'Add or drag an application value directly onto any section.' : activeContentRegion ? `Add or drag an application value as ${activeSection} text.` : 'Choose Header, Body, or Footer first.'}</p>
          {activeContentRegion && values.map(variable => <button
            type="button"
            draggable
            onDragStart={event => {
              event.dataTransfer.effectAllowed = 'copy';
              event.dataTransfer.setData('application/x-certificate-variable', variable.path);
            }}
            disabled={atActiveContentLimit}
            key={variable.path}
            onClick={() => activeContentRegion === 'blocks' ? addComponent('field', { variable }) : addComponent('paragraph', { textMr: `{{${variable.path}}}`, textEn: `{{${variable.path}}}` })}
          ><strong>{variable.label}</strong><small>{variable.path}</small></button>)}
          {activeContentRegion === 'blocks' && photos.length > 0 && <h4>Application photos</h4>}
          {activeContentRegion === 'blocks' && photos.map(variable => <button
            type="button"
            draggable
            onDragStart={event => {
              event.dataTransfer.effectAllowed = 'copy';
              event.dataTransfer.setData('application/x-certificate-block', 'application_photo');
            }}
            disabled={atBlockLimit}
            key={variable.path}
            onClick={() => addComponent('photo', { sourceType: 'field', variable })}
          ><strong>{variable.label}</strong><small>{variable.path}</small></button>)}
        </div>}
      </aside>}

      <main className="certificate-builder-canvas" aria-label="Certificate design canvas">
        <div className="certificate-builder-canvas-note">{canvasHint}</div>
        <div className="certificate-builder-stage-shell" style={{ width: `${820 * previewScale}px`, height: `${1100 * previewScale}px` }}>
          <div className="certificate-builder-stage" style={{ '--certificate-builder-scale': previewScale }}><GovernmentCertificateCard templateSnapshot={{ schema: parseCertificateObject(schema) }} dataSnapshot={previewContext} issuance={{ template_version: draft.version, revision: 1 }} editor={{
            builder: true,
            selectedBlockId,
            selectBlock: selectPreviewBlock,
            update: updateSchemaValue,
            activate: activatePreviewText,
            addBlock: (region, type) => selectNewBlock(type, {}, region),
            moveBlock: moveRegionBlockTo,
            removeBlock: removeRegionBlock,
            updateBlockLayout: (region, index, layout) => {
              const targetRegion = region || 'blocks';
              if (targetRegion === 'header.layout') {
                updateSchemaValue(`header.layout.${index}`, layout);
                return;
              }
              const currentBlocks = Array.isArray(objectPathValue(schema, targetRegion)) ? objectPathValue(schema, targetRegion) : [];
              if (typeof index === 'number' && currentBlocks[index]) {
                const updated = currentBlocks.map((block, i) => (i === index ? { ...block, layout } : block));
                updateSchemaValue(targetRegion, updated);
              }
            },
            updateHeaderLayout: (itemKey, layout) => {
              updateSchemaValue(`header.layout.${itemKey}`, layout);
            },
            dropBlock: (type, options = {}, region = 'blocks') => {
              const targetRegion = region || activeContentRegion || 'blocks';
              const targetSection = targetRegion.startsWith('header')
                ? 'header'
                : targetRegion.startsWith('footer')
                  ? 'footer'
                  : 'body';
              setActiveSection(targetSection);
              const opts = typeof options === 'object' && options !== null && !('x' in options) ? options : { placement: options };
              addComponent(type, opts, targetRegion);
            },
            canAddPhoto: photos.length > 0,
          }} /></div>
        </div>
      </main>

      {inspectorOpen && <aside id="certificate-builder-inspector" className="certificate-builder-inspector">
        <React.Fragment key={selectedBlock ? `block-${selectedBlock.id}` : `section-${activeSection}`}>
          {renderBlockInspector()}
        </React.Fragment>
      </aside>}
    </div>

    <footer className="certificate-builder-footer"><span>Template v{draft.version} · {blocks.length} body components</span><div>{publishedUnlocked ? <button type="button" className="lock" onClick={lockVersion} disabled={busy}>Save &amp; lock version</button> : <button type="button" className="primary" onClick={submitDraft} disabled={busy}>Send for approval</button>}</div></footer>

    {showShortcuts && (
      <div className="certificate-builder-modal-overlay" onClick={() => setShowShortcuts(false)}>
        <div className="certificate-builder-modal-content" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="कीबोर्ड शॉर्टकट मदत">
          <header className="certificate-builder-modal-header">
            <h3>⌨ कीबोर्ड व कॅनव्हास शॉर्टकट्स (Shortcuts Cheat Sheet)</h3>
            <button type="button" onClick={() => setShowShortcuts(false)} aria-label="Close shortcuts modal">×</button>
          </header>
          <div className="certificate-builder-modal-body">
            <table className="certificate-builder-shortcuts-table">
              <thead>
                <tr>
                  <th>शॉर्टकट (Shortcut)</th>
                  <th>कृती (Action)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><kbd>Ctrl</kbd> + <kbd>Z</kbd></td>
                  <td>मागील बदल पूर्ववत करा (Undo)</td>
                </tr>
                <tr>
                  <td><kbd>Ctrl</kbd> + <kbd>Y</kbd> / <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd></td>
                  <td>पुन्हा बदल लागू करा (Redo)</td>
                </tr>
                <tr>
                  <td><kbd>Ctrl</kbd> + <kbd>D</kbd></td>
                  <td>निवडलेला घटक त्वरित डुप्लिकेट करा (Duplicate Component)</td>
                </tr>
                <tr>
                  <td><kbd>Delete</kbd> / <kbd>Backspace</kbd></td>
                  <td>निवडलेला घटक हटवा (Delete Component)</td>
                </tr>
                <tr>
                  <td><kbd>Arrow Keys</kbd></td>
                  <td>घटक १०px ने हलवा (Move with 10px Grid Snap)</td>
                </tr>
                <tr>
                  <td><kbd>Shift</kbd> + <kbd>Arrow Keys</kbd></td>
                  <td>घटक २०px ने जलद हलवा (Move 20px)</td>
                </tr>
                <tr>
                  <td><kbd>Ctrl</kbd> + <kbd>+</kbd> / <kbd>-</kbd></td>
                  <td>झूम वाढवा / कमी करा (Zoom In / Out २५०% पर्यंत)</td>
                </tr>
                <tr>
                  <td><kbd>Ctrl</kbd> + <kbd>0</kbd></td>
                  <td>१००% झूम रिसेट (Reset to 100% Zoom)</td>
                </tr>
                <tr>
                  <td><kbd>Esc</kbd></td>
                  <td>निवड रद्द करा / फुलस्क्रीनमधून बाहेर पडा (Deselect / Exit Fullscreen)</td>
                </tr>
                <tr>
                  <td><kbd>?</kbd></td>
                  <td>ही मदत चौकट उघडा / बंद करा (Toggle Shortcuts Help)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer className="certificate-builder-modal-footer">
            <button type="button" className="primary" onClick={() => setShowShortcuts(false)}>समजले (Got it)</button>
          </footer>
        </div>
      </div>
    )}
  </section>;
}
